import { FC, Ref, useRef } from "react";

import { MoveToSubMenu } from "./MoveToSubMenu";
import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { AddPlusIcon, ArchiveFileBoxIcon, CopySendArrowIcon, PageListStatusIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectCurrentSectionIsTemplate } from "store/selectors";
import { ReleaseStatus } from "utils/activity";

interface ActivitySidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  currentTopicId?: string;
  releaseStatus?: ReleaseStatus;
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onAddPage?: () => void;
    onChangeStatus?: () => void;
    onArchive?: () => void;
    onUnarchive?: () => void;
    onUnarchiveAll?: () => void;
    onMoveTo?: (topicId: string) => void;
  };
}

export const ActivitySidebarOptionsMenu: FC<ActivitySidebarOptionsMenuProps> = ({
  currentTopicId,
  styles,
  className,
  direction = "right",
  releaseStatus,
  optionsMenuActions,
}) => {
  const menuRef: Ref<MenuInstance> = useRef(null);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const onChangeStatus = optionsMenuActions?.onChangeStatus;

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={menuRef}
      optionsMenuActions={optionsMenuActions}
      styles={styles}
    >
      {optionsMenuActions?.onAddPage && (
        <MenuItem eventName="Activity - Add page" onClick={optionsMenuActions.onAddPage}>
          <IconText iconStart={<AddPlusIcon />} text={t("common.add.page")} />
        </MenuItem>
      )}

      {onChangeStatus &&
        !isInTemplateSection &&
        (releaseStatus === "released" ? (
          <MenuItem
            eventName="Activity - Unrelease pages"
            title={t("sidebar_options_menu.tooltip.unrelease_pages")}
            onClick={onChangeStatus}
          >
            <IconText
              iconStart={<PageListStatusIcon statusColor="gray" />}
              text={t("sidebar_options_menu.unrelease_pages")}
            />
          </MenuItem>
        ) : (
          <MenuItem
            disabled={!releaseStatus}
            eventName="Activity - Release pages"
            title={t(
              `sidebar_options_menu.tooltip.${releaseStatus ? "release_pages" : "no_pages"}`,
            )}
            onClick={onChangeStatus}
          >
            <IconText
              iconStart={<PageListStatusIcon statusColor="green" />}
              text={t("sidebar_options_menu.release_pages")}
            />
          </MenuItem>
        ))}

      <MenuDivider />
      {optionsMenuActions?.onArchive && (
        <MenuItem eventName="Activity - Archive" onClick={optionsMenuActions.onArchive}>
          <IconText iconStart={<ArchiveFileBoxIcon />} text={t("sidebar_options_menu.archive")} />
        </MenuItem>
      )}
      {optionsMenuActions?.onUnarchive && (
        <MenuItem eventName="Activity - Unarchive" onClick={optionsMenuActions.onUnarchive}>
          <IconText iconStart={<ArchiveFileBoxIcon />} text={t("sidebar_options_menu.unarchive")} />
        </MenuItem>
      )}
      {optionsMenuActions?.onUnarchiveAll && (
        <MenuItem eventName="Activity - Unarchive all" onClick={optionsMenuActions.onUnarchiveAll}>
          <IconText
            iconStart={<ArchiveFileBoxIcon />}
            text={t("sidebar_options_menu.unarchive_all")}
          />
        </MenuItem>
      )}
      {optionsMenuActions?.onMoveTo && (
        <SubMenu
          label={
            <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
          }
          overflow="auto"
          withArrow
        >
          <MoveToSubMenu
            currentTopicId={currentTopicId}
            itemKind="activity"
            onCancel={() => {
              menuRef.current?.closeMenu();
            }}
            onConfirm={(topicId) => {
              if (topicId) optionsMenuActions?.onMoveTo?.(topicId);
              menuRef.current?.closeMenu();
            }}
          />
        </SubMenu>
      )}
    </SidebarOptionsMenu>
  );
};
